import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { Camera, X, ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

function buildReader() {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8]);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return new BrowserMultiFormatReader(hints);
}

export default function IsbnScanner({ open, onOpenChange, onDetected }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [error, setError] = useState("");
  const [hint, setHint] = useState("");

  useEffect(() => {
    if (!open) return;
    let active = true;
    setError(""); setHint("");
    const reader = buildReader();

    (async () => {
      try {
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } } },
          videoRef.current,
          (result) => {
            if (!result || !active) return;
            const text = (result.getText() || "").replace(/[^0-9Xx]/g, "");
            if (text.length === 13 && (text.startsWith("978") || text.startsWith("979"))) {
              active = false;
              controls.stop();
              onDetected(text);
              onOpenChange(false);
            } else if (text.length === 10) {
              active = false;
              controls.stop();
              onDetected(text);
              onOpenChange(false);
            } else if (text) {
              setHint("Codice letto ma non sembra un ISBN di un libro. Inquadra il codice a barre sul retro (inizia con 978/979).");
            }
          }
        );
        controlsRef.current = controls;
      } catch (e) {
        if (!active) return;
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          setError("Permesso fotocamera negato. Consenti l'accesso alla fotocamera e riprova.");
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setError("Nessuna fotocamera disponibile su questo dispositivo.");
        } else {
          setError("Impossibile avviare la fotocamera. Riprova o inserisci l'ISBN manualmente.");
        }
      }
    })();

    return () => {
      active = false;
      try { controlsRef.current?.stop(); } catch (_) {}
      controlsRef.current = null;
    };
  }, [open, onDetected, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="isbn-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2"><Camera className="w-5 h-5 text-primary" />Scansiona il codice ISBN</DialogTitle>
          <DialogDescription>Inquadra il codice a barre sul retro del libro (EAN-13, inizia con 978 o 979).</DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="py-10 text-center text-sm text-destructive px-4" data-testid="scanner-error">{error}</div>
        ) : (
          <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline data-testid="scanner-video" />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[80%] h-24 border-2 border-white/80 rounded-lg relative">
                <ScanLine className="absolute inset-x-0 top-1/2 -translate-y-1/2 mx-auto w-[70%] h-8 text-primary/80 animate-pulse" />
              </div>
            </div>
          </div>
        )}
        {hint && <p className="text-xs text-amber-600 flex items-start gap-1.5 px-1" data-testid="scanner-hint"><X className="w-3.5 h-3.5 mt-0.5 shrink-0" />{hint}</p>}
      </DialogContent>
    </Dialog>
  );
}
